import { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Save, Eye, Globe, Upload, Plus, Trash2,
  GripVertical, Image, Video, Music, Calendar, Mail,
  User, Layout, Palette, Settings, ExternalLink, Newspaper, Copy, Quote, Star,
  Briefcase, Users, HelpCircle, MessageSquare, Phone, CheckCircle2, XCircle, AlertTriangle, Link, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import CloudinaryUploader, { GalleryUploader } from '@/components/CloudinaryUploader';

export const FAQModuleEditor = ({ data, onChange }) => {
  const faqs = data.faqs || [];
  const [editingIdx, setEditingIdx] = useState(null);
  
  const addFAQ = () => {
    const newFAQ = { id: Date.now().toString(), question: 'New Question', answer: '' };
    onChange({...data, faqs: [...faqs, newFAQ]});
    setEditingIdx(faqs.length);
  };
  
  const updateFAQ = (idx, updates) => {
    const updated = [...faqs];
    updated[idx] = {...updated[idx], ...updates};
    onChange({...data, faqs: updated});
  };
  
  const removeFAQ = (idx) => {
    onChange({...data, faqs: faqs.filter((_, i) => i !== idx)});
    setEditingIdx(null);
  };

  const renderEditor = (item, idx) => (
    <div className="bg-slate-800 border border-slate-600 rounded-lg p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-white">Edit FAQ</h4>
        <Button variant="ghost" size="sm" onClick={() => setEditingIdx(null)} className="text-slate-400 hover:text-white">Done</Button>
      </div>
      
      <div className="space-y-2">
        <Label>Question</Label>
        <Input value={item.question} onChange={(e) => updateFAQ(idx, { question: e.target.value })} placeholder="What do people often ask?" />
      </div>
      
      <div className="space-y-2">
        <Label>Answer</Label>
        <Textarea value={item.answer || ''} onChange={(e) => updateFAQ(idx, { answer: e.target.value })} placeholder="Your answer..." rows={4} />
      </div>

      <div className="pt-2 border-t border-slate-700">
        <Button variant="ghost" size="sm" onClick={() => removeFAQ(idx)} className="text-red-400 hover:text-red-300 hover:bg-red-500/20">
          <Trash2 className="w-4 h-4 mr-2" /> Delete FAQ
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Section Title</Label>
        <Input value={data.title || 'FAQ'} onChange={(e) => onChange({...data, title: e.target.value})} />
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>Questions ({faqs.length})</Label>
          <Button variant="outline" size="sm" onClick={addFAQ}><Plus className="w-4 h-4 mr-1" /> Add FAQ</Button>
        </div>
        
        {faqs.length === 0 ? (
          <div className="text-center py-12 bg-slate-700/30 rounded-lg border border-slate-600">
            <HelpCircle className="w-10 h-10 mx-auto text-slate-500 mb-3" />
            <p className="text-sm text-slate-400">No FAQs yet. Add common questions!</p>
          </div>
        ) : editingIdx !== null && faqs[editingIdx] ? (
          renderEditor(faqs[editingIdx], editingIdx)
        ) : (
          <div className="space-y-2">
            {faqs.map((item, idx) => (
              <div key={item.id || idx} onClick={() => setEditingIdx(idx)} className="flex items-center gap-4 p-3 bg-slate-700/30 border border-slate-600 rounded-lg cursor-pointer hover:bg-slate-700/50 hover:border-slate-500 transition-all group">
                <HelpCircle className="w-5 h-5 text-orange-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-white truncate">{item.question || 'Untitled'}</h4>
                  <p className="text-xs text-slate-400 truncate">{item.answer?.substring(0, 60) || 'No answer yet'}...</p>
                </div>
                <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); removeFAQ(idx); }} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 hover:bg-red-500/20 h-8 w-8 p-0"><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Testimonials Module Editor

